import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Typography } from '@mui/material';

import { Messages, ShadowCard } from 'src/components/base/core';
import { ManualLoader } from '../../../components/base/core/OtherUtilities/Loader/ManualLoader';
import {
  useGetUserQuery,
  useUpdateMyEmailMutation,
} from '../../../services/account/accountServiceApi';
import { useCreateVerificationCodeMutation } from '../../../services/auth/authServiceApi';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Dedicated "Login email" section in the profile.
 *
 * The email a user signs in with. Editing it is a security-sensitive action, so
 * it is gated behind step-up 2FA: a code is sent to the user's CURRENT email and
 * confirmed server-side against that same address before the change is applied.
 * On success the user must sign in with the new email (see the warning copy).
 *
 * Self-scoped: targets whoever is logged in (base or added user) via the JWT on
 * the server — never the account-level email of someone else.
 */
export const AccountEmailSection = ({
  isCardBorderEnabled = false,
}: {
  isCardBorderEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { data: userData } = useGetUserQuery({});
  const currentEmail: string = userData?.email ?? '';

  const [editing, setEditing] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [sendCode, { isLoading: isSending }] = useCreateVerificationCodeMutation();
  const [updateMyEmail, { isLoading: isSaving }] = useUpdateMyEmailMutation();

  const reset = () => {
    setEditing(false);
    setCodeSent(false);
    setNewEmail('');
    setCode('');
    setError('');
  };

  const onSendCode = async () => {
    setError('');
    if (!isValidEmail(newEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (newEmail.trim().toLowerCase() === currentEmail.toLowerCase()) {
      setError('The new email is the same as your current email.');
      return;
    }
    // Send the code to the user's CURRENT email (proves it's really them).
    const res: any = await sendCode(currentEmail);
    if (res?.error) {
      setError('Could not send the verification code. Please try again.');
      return;
    }
    setCodeSent(true);
  };

  const onSave = async () => {
    setError('');
    if (!code) {
      setError('Please enter the verification code.');
      return;
    }
    const res: any = await updateMyEmail({
      email: newEmail.trim(),
      verification_code: code.trim(),
    });
    if (res?.error) {
      const msg =
        res.error?.data?.message ||
        res.error?.data?.error ||
        (typeof res.error?.data === 'string' ? res.error.data : '') ||
        'Could not update your email. Check the code and try again.';
      setError(msg);
      return;
    }
    setSuccess('Your email was updated. Use your new email to log in from now on.');
    reset();
  };

  return (
    <ShadowCard
      display={'flex'}
      flexDirection={'column'}
      paddingLeft={isCardBorderEnabled ? 0 : 4}
      paddingRight={isCardBorderEnabled ? 0 : 4}
      paddingTop={isCardBorderEnabled ? 0 : 4}
      paddingBottom={isCardBorderEnabled ? 0 : 1}
      marginTop={isCardBorderEnabled ? 0 : 6}
      marginBottom={isCardBorderEnabled ? 0 : 4}
      className={isCardBorderEnabled ? 'remove_all_shadow' : 'normal_shadow'}
    >
      {(isSending || isSaving) && <ManualLoader />}

      <Typography
        textTransform={'uppercase'}
        variant="caption2"
        color={'gray.500'}
        marginBottom={2}
      >
        {t('formInput.email')}
      </Typography>

      {!editing && (
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant="h1" color={'gray.700'}>
            {currentEmail}
          </Typography>
          <Button
            variant="text"
            color="primary"
            sx={{ fontWeight: 600 }}
            onClick={() => {
              setSuccess('');
              setEditing(true);
            }}
          >
            {t('buttonTexts.editButton')}
          </Button>
        </Box>
      )}

      {!editing && success && (
        <Messages topMargin={2} bottomMargin={2} messageHeading={''} messageContent={success} />
      )}

      {editing && (
        <Box>
          <Messages
            topMargin={0}
            bottomMargin={4}
            messageHeading={'This is also your login email'}
            messageContent={
              'If you change it, you’ll need to sign in with the new email from now on. ' +
              'For your security, we’ll send a verification code to your current email first.'
            }
          />

          <TextField
            fullWidth
            label={'New email'}
            value={newEmail}
            disabled={codeSent}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ marginBottom: 3 }}
          />

          {!codeSent && (
            <Button
              variant="containedLarge"
              color="primary"
              onClick={onSendCode}
              sx={{ width: '100%' }}
            >
              {isSending ? 'Sending…' : 'Send verification code'}
            </Button>
          )}

          {codeSent && (
            <>
              <Typography variant="h1" color={'gray.700'} marginBottom={2}>
                {`Enter the code we sent to ${currentEmail}`}
              </Typography>
              <TextField
                fullWidth
                label={'Verification code'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                sx={{ marginBottom: 3 }}
              />
              <Button
                variant="containedLarge"
                color="primary"
                onClick={onSave}
                sx={{ width: '100%' }}
              >
                {isSaving ? 'Saving…' : 'Save email'}
              </Button>
            </>
          )}

          {error && (
            <Messages
              topMargin={4}
              bottomMargin={0}
              messageHeading={'Error'}
              messageContent={error}
              closeEvent={() => setError('')}
            />
          )}

          <Box marginTop={2}>
            <Button variant="text" color="primary" sx={{ fontWeight: 600 }} onClick={reset}>
              {t('buttonTexts.cancelButton')}
            </Button>
          </Box>
        </Box>
      )}
    </ShadowCard>
  );
};
